/**
 * Settings shape. Stored as a single JSON blob — 02-data-model.md §8.
 *
 * Only the fields Phase 0 actually uses are wired up; the rest are declared so
 * the type is complete and later phases just fill them in. Adding a setting
 * needs no migration precisely because this is one blob.
 */

export type ThemeStyle = 'pixel' | 'sleek'
export type ColorSchemeSetting = 'system' | 'light' | 'dark'
export type BlockHeightScale = 'compact' | 'normal' | 'large'
export type LabelTruncation = 'head' | 'middle' | 'tail'
export type NowMarkerStyle = 'classic' | 'modern'
export type MarkAnimation = 'none' | 'fade' | 'wipe' | 'pop' | 'pixel'
export type PrecisionMinutes = 5 | 10 | 15 | 30 | 60

export interface AppSettings {
  // Tracking
  precisionMinutes: PrecisionMinutes
  dayStartHour: number
  cursorMode: boolean
  skipOccupiedOnMultiMark: boolean
  futureDefaultsToPlanned: boolean

  // Appearance
  themeStyle: ThemeStyle
  colorScheme: ColorSchemeSetting
  colorThemeId: string
  colorblindMode: boolean
  blockCornerRadius: number | null
  block3DEffect: boolean
  blockHeightScale: BlockHeightScale
  labelTruncation: LabelTruncation
  nowMarkerStyle: NowMarkerStyle
  showHourGridlines: boolean
  showDateIntervalLabels: boolean
  showHowLongAgo: boolean
  toolbarAutoScroll: boolean
  hideTypeWhenObjectSet: boolean
  hideTypeAndObjectWhenComment: boolean
  scrollByPage: boolean

  // Feedback (no haptics — 03-ui-spec.md §2.6)
  markAnimation: MarkAnimation
  animationSpeed: number
  soundEnabled: boolean

  // Data
  syncEnabled: boolean
  lastExportAt: number | null

  // Security
  lockEnabled: boolean
  passwordHint: string | null
}

export const DEFAULT_SETTINGS: AppSettings = {
  precisionMinutes: 30,
  dayStartHour: 0,
  cursorMode: true,
  skipOccupiedOnMultiMark: true,
  futureDefaultsToPlanned: true,

  themeStyle: 'pixel',
  colorScheme: 'system',
  colorThemeId: 'default',
  colorblindMode: false,
  blockCornerRadius: null,
  block3DEffect: true,
  blockHeightScale: 'normal',
  labelTruncation: 'tail',
  nowMarkerStyle: 'classic',
  showHourGridlines: true,
  showDateIntervalLabels: true,
  showHowLongAgo: true,
  toolbarAutoScroll: true,
  hideTypeWhenObjectSet: false,
  hideTypeAndObjectWhenComment: false,
  scrollByPage: false,

  markAnimation: 'fade',
  animationSpeed: 1.0,
  soundEnabled: false,

  syncEnabled: true,
  lastExportAt: null,

  lockEnabled: false,
  passwordHint: null,
}
